import { useState } from 'react';
import { MSym } from '@storeo/ui';
import { useAuth } from '../../lib/auth';
import { PlaceholderTag } from '../lumina/Placeholder';

const BENEFITS = [
  { icon: 'space_dashboard', color: 'text-primary', title: 'Cockpit 360°', desc: 'GMV, comissão, assinaturas e funil de conversão da plataforma inteira.' },
  { icon: 'storefront', color: 'text-secondary', title: 'Lojas & Subdomínios', desc: 'Todo parceiro cadastrado, com a loja e o GMV de cada um.' },
  { icon: 'verified_user', color: 'text-tertiary', title: 'Auditoria & Logs', desc: 'Lojas paradas em rascunho, chargeback fora da curva, campanha falhando.' },
];

/** Tela única de login — a SPA inteira é atrás do login, não tem rota pública (mesmo padrão de
 *  `apps/painel`). Formulário embutido (grant direto no Keycloak, client `storeo-admin`, role
 *  `platform-staff`). Redesign Lumina a partir do protótipo "Login Master Command" — dropado
 *  o hardware key (YubiKey/FIDO2) e a "frase-mestra HSM", que não existem; só e-mail+senha. */
export function LoginScreen() {
  const { login, error, status } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="lumina w-full min-h-screen bg-surface text-on-surface flex items-center justify-center p-space-md">
      <div className="flex flex-col w-full max-w-5xl mx-auto relative px-space-sm sm:px-space-md py-space-xl">
        <div className="absolute -top-32 -left-20 w-96 h-96 rounded-full bg-primary-container/20 blur-3xl pointer-events-none -z-10" />
        <div className="absolute -bottom-32 -right-20 w-96 h-96 rounded-full bg-tertiary-container/20 blur-3xl pointer-events-none -z-10" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-space-lg items-stretch">
          <div className="lg:col-span-5 flex flex-col justify-between bg-surface-container-low/90 backdrop-blur-xl rounded-xl p-space-lg sm:p-space-xl shadow-2xl order-2 lg:order-1">
            <div className="flex flex-col gap-space-md">
              <div className="inline-flex items-center gap-space-xs self-start px-space-sm py-space-2xs rounded-full bg-surface-container-high/80 text-secondary font-label-sm text-label-sm uppercase tracking-wider">
                <span className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
                Painel interno
              </div>
              <div>
                <h2 className="text-white font-headline-lg text-headline-lg tracking-tight">Storeo Admin</h2>
                <p className="text-on-surface-variant font-body-md text-body-md mt-space-xs">Retrato da plataforma pra equipe Storeo — parceiros, lojas e sinais de saúde.</p>
              </div>
              <div className="flex flex-col gap-space-sm mt-space-sm">
                {BENEFITS.map((b) => (
                  <div key={b.title} className="flex items-start gap-space-sm p-space-sm rounded-lg bg-surface-container/60">
                    <div className={`p-space-xs rounded bg-surface-container-high ${b.color} flex items-center justify-center shrink-0`}>
                      <MSym name={b.icon} className="text-lg" fill />
                    </div>
                    <div>
                      <p className="text-white font-label-lg text-label-lg">{b.title}</p>
                      <p className="text-on-surface-variant font-body-sm text-body-sm">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 flex flex-col justify-center bg-surface-container/80 backdrop-blur-2xl rounded-xl p-space-lg sm:p-space-2xl shadow-2xl order-1 lg:order-2">
            <div className="flex flex-col gap-space-2xs mb-space-lg">
              <div className="flex items-center gap-space-xs mb-space-2xs">
                <div className="w-8 h-8 rounded-lg bg-linear-to-br from-primary to-tertiary flex items-center justify-center text-on-primary">
                  <MSym name="terminal" className="text-lg" fill />
                </div>
                <span className="text-white font-label-lg text-label-lg tracking-wider font-bold uppercase">Storeo Admin</span>
              </div>
              <h1 className="text-white font-headline-lg text-headline-lg leading-tight">Acesso da equipe</h1>
              <p className="text-on-surface-variant font-body-md text-body-md">Entre com sua conta de equipe (role platform-staff) para acessar a visão da plataforma.</p>
            </div>

            <div className="flex p-space-3xs bg-surface-container-low rounded-lg mb-space-lg">
              <span className="flex-1 py-space-xs px-space-sm rounded font-label-md text-label-md text-white bg-surface-container-high flex items-center justify-center gap-space-2xs">
                <MSym name="lock" className="text-sm" />
                E-mail e Senha
              </span>
              <span className="flex-1 py-space-xs px-space-sm rounded font-label-md text-label-md text-on-surface-variant flex items-center justify-center gap-space-2xs opacity-60">
                <MSym name="fingerprint" className="text-sm text-tertiary" />
                Chave de Segurança
                <PlaceholderTag />
              </span>
            </div>

            <form
              className="flex flex-col gap-space-md"
              onSubmit={(e) => {
                e.preventDefault();
                login(email, password);
              }}
            >
              <div className="flex flex-col gap-space-2xs">
                <label className="text-white font-label-md text-label-md" htmlFor="admin-email">
                  E-mail Corporativo
                </label>
                <div className="relative flex items-center">
                  <MSym name="alternate_email" className="absolute left-3 text-on-surface-variant" />
                  <input
                    id="admin-email"
                    className="w-full bg-surface-container-lowest text-white placeholder-outline rounded-lg pl-10 pr-4 py-3 font-body-md text-body-md focus:outline-none focus:ring-2 focus:ring-primary shadow-inner"
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-space-2xs">
                <label className="text-white font-label-md text-label-md" htmlFor="admin-password">
                  Senha
                </label>
                <div className="relative flex items-center">
                  <MSym name="lock" className="absolute left-3 text-on-surface-variant" />
                  <input
                    id="admin-password"
                    className="w-full bg-surface-container-lowest text-white placeholder-outline rounded-lg pl-10 pr-10 py-3 font-body-md text-body-md focus:outline-none focus:ring-2 focus:ring-primary shadow-inner"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button type="button" className="absolute right-3 text-on-surface-variant hover:text-white flex items-center" onClick={() => setShowPassword((v) => !v)}>
                    <MSym name={showPassword ? 'visibility_off' : 'visibility'} className="text-lg" />
                  </button>
                </div>
              </div>
              {error ? <p className="rounded-lg bg-error-container/25 text-on-error-container px-space-md py-space-sm font-body-sm text-body-sm">{error}</p> : null}
              <button
                className="w-full py-space-sm px-space-lg rounded-lg bg-linear-to-r from-primary via-primary-container to-secondary text-white font-label-lg text-label-lg font-bold tracking-wide flex items-center justify-center gap-space-xs shadow-xl hover:scale-[1.01] active:scale-[0.99] transition-all mt-space-xs disabled:opacity-60 disabled:pointer-events-none"
                type="submit"
                disabled={status === 'checking'}
              >
                <span>{status === 'checking' ? 'Entrando…' : 'Entrar'}</span>
                <MSym name="arrow_forward" className="text-base" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
